import type { Column } from '@tanstack/react-table'

export const sanitizeColumnId = (id: string) =>
  id.toLowerCase().replace(/\s+/g, '-')

// Interactive cell content (e.g. the search waveform) marks itself with
// data-row-select-ignore so its clicks play/seek without selecting the row.
const ROW_SELECT_IGNORE_SELECTOR = '[data-row-select-ignore]'

export const isRowSelectIgnored = (target: EventTarget | null): boolean =>
  target instanceof Element && !!target.closest(ROW_SELECT_IGNORE_SELECTOR)

// Pinned columns are sticky on horizontal scroll by default;
// meta.enableSticky === false opts a pinned column out, keeping its fixed
// order and drag lock without sticking (e.g. Artist/Source vs select/Title).
// meta is read via an explicit shape: its augmentation lives in frontend's
// tanstack-table.d.ts, which the cl strict program doesn't compile.
export const isStickyPinned = <T>(column: Column<T>): boolean => {
  const meta = column.columnDef.meta as { enableSticky?: boolean } | undefined
  return !!column.getIsPinned() && meta?.enableSticky !== false
}

// Inserts columnId after anchorId in a saved order that predates it.
export const insertColumnAfter = (
  order: string[],
  columnId: string,
  anchorId: string,
): string[] => {
  if (order.length === 0 || order.includes(columnId)) return order
  const anchorIndex = order.indexOf(anchorId)
  if (anchorIndex === -1) return [...order, columnId]
  return [
    ...order.slice(0, anchorIndex + 1),
    columnId,
    ...order.slice(anchorIndex + 1),
  ]
}

// Inserts columnId immediately before beforeId in a saved order that
// predates it.
const insertColumnBefore = (
  order: string[],
  columnId: string,
  beforeId: string,
): string[] => {
  if (order.length === 0 || order.includes(columnId)) return order
  const beforeIndex = order.indexOf(beforeId)
  if (beforeIndex === -1) return [...order, columnId]
  return [...order.slice(0, beforeIndex), columnId, ...order.slice(beforeIndex)]
}

// Chains enabled entries after each other, anchored on the first-present
// `baseAnchorCandidates` entry — pass candidates most-preferred-first. If a
// later entry is already present (e.g. it settled into a position before an
// earlier entry existed to chain off), the earlier one is inserted right
// before it instead of blindly anchoring on the base — so the chain's
// relative order holds even when it's assembled out of order over time.
export const insertColumnChain = (
  order: string[],
  baseAnchorCandidates: string[],
  chain: Array<{ id: string; enabled: boolean }>,
): string[] => {
  const enabledChain = chain.filter(entry => entry.enabled)
  // The `?? ''` never actually fires — every call site passes a non-empty
  // baseAnchorCandidates — but noUncheckedIndexedAccess can't prove that
  // from `baseAnchorCandidates[0]` alone, so it needs a same-type fallback.
  let anchor: string =
    baseAnchorCandidates.find(id => order.includes(id)) ??
    baseAnchorCandidates[0] ??
    ''
  let result = order
  for (const [i, { id }] of enabledChain.entries()) {
    if (!result.includes(id)) {
      const nextPresent = enabledChain
        .slice(i + 1)
        .find(entry => result.includes(entry.id))
      result = nextPresent
        ? insertColumnBefore(result, id, nextPresent.id)
        : insertColumnAfter(result, id, anchor)
    }
    anchor = id
  }
  return result
}

// Reconciles a persisted column order against the table's live leaf columns:
export const reconcileColumnOrder = (
  savedOrder: string[],
  leafIds: string[],
): string[] => {
  const kept = savedOrder.filter(id => leafIds.includes(id))
  return [...kept, ...leafIds.filter(id => !kept.includes(id))]
}

// Moves draggedId to targetId's position within currentOrder. Returns the new
// order, or null when the move is a no-op (either id missing, or same position).
export const reorderColumns = (
  currentOrder: string[],
  draggedId: string,
  targetId: string,
): string[] | null => {
  const from = currentOrder.indexOf(draggedId)
  const to = currentOrder.indexOf(targetId)
  if (from === -1 || to === -1 || from === to) return null

  const next = [...currentOrder]
  next.splice(from, 1)
  next.splice(to, 0, draggedId)
  return next
}
